/**
 * 🔐 RBAC Context - Cognito as Single Source of Truth
 * 
 * Permissions are stored in Cognito custom:permissions claim
 * Extracted from JWT on login - no API calls needed
 * When role permissions change, user gets new permissions on next login
 * 
 * This is the ONLY source of truth for permissions - Cognito
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import {
  getPermissionsFromJWT,
  getRoleFromJWT,
} from '../utils/JWTUtils';

const RBACContext = createContext();

export const RBACProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize RBAC on component mount
   * Extracts role and permissions from JWT (Cognito custom claims)
   * NO API CALL NEEDED - Cognito is the single source of truth
   */
  const initializeRBAC = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Get current Cognito user
      const authUser = await Auth.currentAuthenticatedUser();
      console.log('✓ Authenticated user:', authUser.username);
      setUser(authUser);

      // Step 2: Extract role from JWT custom:role claim
      const role = await getRoleFromJWT();
      if (!role) {
        throw new Error(
          'No role found in JWT. Please ensure custom:role attribute is set in Cognito for this user.'
        );
      }
      console.log('✓ User role from Cognito:', role);
      setUserRole(role);

      // Step 3: Extract permissions from JWT custom:permissions claim
      const perms = await getPermissionsFromJWT();
      if (perms && perms.length > 0) {
        console.log(`✓ Loaded ${perms.length} permissions from Cognito JWT`);
        setPermissions(perms);
        // Cache in sessionStorage for offline access
        sessionStorage.setItem('userPermissions', JSON.stringify(perms));
      } else {
        console.warn(
          `⚠ No permissions found in JWT custom:permissions claim. ` +
          `This usually means admin hasn't synced permissions to Cognito yet for this role. ` +
          `User will have empty permissions until admin syncs their role.`
        );
        // No fallback - Cognito is the single source of truth
        setPermissions([]);
        sessionStorage.setItem('userPermissions', JSON.stringify([]));
      }

      // Cache user profile in sessionStorage
      sessionStorage.setItem('userRole', role);
      sessionStorage.setItem('userProfile', JSON.stringify({
        email: authUser.attributes?.email,
        name: authUser.attributes?.name,
        role: role,
      }));

      setLoading(false);
    } catch (err) {
      console.error('Error initializing RBAC:', err.message);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    initializeRBAC();
  }, [initializeRBAC]);


  /**
   * Check if user has a single permission
   * Example: hasPermission('createRFQ')
   */
  const hasPermission = useCallback(
    (permission) => {
      if (!permission || !Array.isArray(permissions)) return false;
      return permissions.includes(permission);
    },
    [permissions]
  );

  /**
   * Check if user has ALL permissions
   * Example: hasAllPermissions(['createRFQ', 'approveRFQ'])
   */
  const hasAllPermissions = useCallback(
    (requiredPermissions) => {
      if (!Array.isArray(requiredPermissions)) return false;
      return requiredPermissions.every((perm) => permissions.includes(perm));
    },
    [permissions]
  );

  /**
   * Check if user has ANY permission
   * Example: hasAnyPermission(['approveRFQ', 'approveOrder'])
   */
  const hasAnyPermission = useCallback(
    (requiredPermissions) => {
      if (!Array.isArray(requiredPermissions)) return false;
      return requiredPermissions.some((perm) => permissions.includes(perm));
    },
    [permissions]
  );

  /**
   * Check if user's role matches
   * Example: hasRole('seller-admin')
   */
  const hasRole = useCallback(
    (role) => {
      return userRole === role;
    },
    [userRole]
  );

  /**
   * Check if user has ANY of the roles
   * Example: hasAnyRole(['admin', 'seller-admin'])
   */
  const hasAnyRole = useCallback(
    (requiredRoles) => {
      if (!Array.isArray(requiredRoles)) return false;
      return requiredRoles.includes(userRole);
    },
    [userRole]
  );

  const value = {
    // State
    user,
    userRole,
    permissions,
    loading,
    error,

    // Methods
    initializeRBAC,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

/**
 * Hook to use RBAC context
 * 
 * Example:
 * const { hasPermission, permissions, userRole } = useRBAC();
 * 
 * if (hasPermission('createRFQ')) {
 *   return <CreateRFQButton />;
 * }
 */
export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within RBACProvider');
  }
  return context;
};
export default RBACContext;